import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { lobbyDayApi } from '../lib/api'
import type { LinkBillRequest } from '../types/lobbyDay'

// The reconciliation query reads an ask's linked bill (AskReconciliationDto.billNumber), and the
// event-detail query (useLobbyDayEvent, ['lobby-day', 'events', eventId]) now carries
// LobbyDayAskDto.linkedBills for chip hydration in AskEditorCard -- every bill-linking mutation
// invalidates both on settle. If the bill picker ever gets its own dedicated cache key, invalidate
// that here too.
function invalidateAskBills(qc: QueryClient, eventId: string) {
  qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
  qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
}

export function useBillSearch(eventId: string, askId: string, q: string, sessionId?: number) {
  return useQuery({
    queryKey: ['lobby-day', 'ask-bills', 'search', eventId, askId, q, sessionId ?? null],
    queryFn: async () => (await lobbyDayApi.searchBills(eventId, askId, q, sessionId)).data,
    enabled: !!eventId && !!askId && q.trim().length > 0,
  })
}

export function useLinkBill(eventId: string, askId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: LinkBillRequest) => (await lobbyDayApi.linkBill(eventId, askId, body)).data,
    onSuccess: () => invalidateAskBills(qc, eventId),
  })
}

export function useUnlinkBill(eventId: string, askId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (askBillId: string) => {
      await lobbyDayApi.unlinkBill(eventId, askId, askBillId)
    },
    onSuccess: () => invalidateAskBills(qc, eventId),
  })
}

export function useRefreshBills(eventId: string, askId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await lobbyDayApi.refreshBills(eventId, askId)).data,
    onSuccess: () => invalidateAskBills(qc, eventId),
  })
}
